import {
  Button,
  Card,
  DatePicker,
  Input,
  message,
  Segmented,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import LayoutWithMenu from 'components/Layout/LayoutWithMenu';
import dayjs, { type Dayjs } from 'dayjs';
import { useScreen } from 'hooks/useScreen';
import { useCallback, useEffect, useState } from 'react';

import CashTransactionDrawer from './CashTransactionDrawer';
import type { CashKind, CashPage, CashRow } from './types';
import { formatMNT, KIND_LABEL, queryErrorMessage } from './types';

const { Title } = Typography;
const { RangePicker } = DatePicker;

type Range = [Dayjs, Dayjs];
const today = (): Range => [dayjs().startOf('day'), dayjs().startOf('day')];

// "Кассын гүйлгээ" (CONTEXT.md: Cash transaction): the cash desk's deposits
// and withdrawals for a date range, with the range's totals.
export default function CashTransactions() {
  const { level, runQuery } = useScreen();
  const canEdit = level === 'edit';

  const [range, setRange] = useState<Range>(today);
  const [kind, setKind] = useState<CashKind | 'ALL'>('ALL');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [data, setData] = useState<CashPage | null>(null);
  const [loading, setLoading] = useState(false);
  const [drawerKind, setDrawerKind] = useState<CashKind | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const load = useCallback(() => {
    setLoading(true);
    runQuery<CashPage>('cash_transactions', {
      from: range[0].startOf('day').toISOString(),
      to: range[1].add(1, 'day').startOf('day').toISOString(),
      kind: kind === 'ALL' ? null : kind,
      search: search.trim() || null,
      page,
      pageSize,
    })
      .then(([row]) => setData(row ?? null))
      .catch((e) => message.error(queryErrorMessage(e)))
      .finally(() => setLoading(false));
  }, [runQuery, range, kind, search, page, pageSize]);

  useEffect(load, [load, reloadKey]);

  const handleDone = () => {
    setDrawerKind(null);
    const now = dayjs();
    const showsToday = !now.isBefore(range[0], 'day') && !now.isAfter(range[1], 'day');
    if (!showsToday) setRange(today());
    setPage(0);
    setReloadKey((k) => k + 1);
  };

  const columns: ColumnsType<CashRow> = [
    {
      title: 'Огноо',
      dataIndex: 'transaction_date',
      width: 160,
      render: (value: string) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '—'),
    },
    { title: 'Сурагч', dataIndex: 'student_name' },
    { title: 'Анги', dataIndex: 'class_name', render: (value: string | null) => value ?? '—' },
    {
      title: 'Төрөл',
      dataIndex: 'kind',
      render: (value: CashKind) => (
        <Tag color={value === 'DEPOSIT' ? 'green' : 'red'}>{KIND_LABEL[value]}</Tag>
      ),
    },
    {
      title: 'Дүн',
      dataIndex: 'amount',
      align: 'right',
      render: (value: number, row) => (
        <span style={{ color: row.kind === 'DEPOSIT' ? '#389e0d' : '#cf1322' }}>
          {row.kind === 'DEPOSIT' ? '+' : '−'}
          {formatMNT(value)}
        </span>
      ),
    },
    { title: 'Өмнөх үлдэгдэл', dataIndex: 'before_balance', align: 'right', render: formatMNT },
    { title: 'Дараах үлдэгдэл', dataIndex: 'after_balance', align: 'right', render: formatMNT },
    {
      title: 'Тайлбар',
      dataIndex: 'description',
      render: (value: string | null, row) => (
        <span>
          {row.batch_id != null && <Tag>Багц #{row.batch_id}</Tag>}
          {value ?? '—'}
        </span>
      ),
    },
    {
      title: 'Бүртгэсэн',
      dataIndex: 'created_by_name',
      render: (value: string | null) => value ?? '—',
    },
  ];

  const depositTotal = data?.deposit_total ?? 0;
  const withdrawalTotal = data?.withdrawal_total ?? 0;

  return (
    <LayoutWithMenu className="px-3 lg:px-5">
      <Card>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 12,
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 16,
          }}
        >
          <Title level={4} style={{ margin: 0 }}>
            Кассын гүйлгээ
          </Title>
          {canEdit && (
            <div style={{ display: 'flex', gap: 8 }}>
              <Button type="primary" onClick={() => setDrawerKind('DEPOSIT')}>
                Орлого
              </Button>
              <Button type="primary" danger onClick={() => setDrawerKind('WITHDRAWAL')}>
                Зарлага
              </Button>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
          <RangePicker
            value={range}
            allowClear={false}
            onChange={(value) => {
              if (value?.[0] && value[1]) {
                setRange([value[0], value[1]]);
                setPage(0);
              }
            }}
          />
          <Segmented
            value={kind}
            onChange={(value) => {
              setKind(value as CashKind | 'ALL');
              setPage(0);
            }}
            options={[
              { label: 'Бүгд', value: 'ALL' },
              { label: 'Орлого', value: 'DEPOSIT' },
              { label: 'Зарлага', value: 'WITHDRAWAL' },
            ]}
          />
          <Input.Search
            placeholder="Сурагчийн нэрээр хайх"
            allowClear
            style={{ width: 240 }}
            onSearch={(value) => {
              setSearch(value);
              setPage(0);
            }}
          />
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 32, marginBottom: 16 }}>
          <Statistic
            title="Орлого"
            value={depositTotal}
            formatter={(v) => formatMNT(Number(v))}
            valueStyle={{ color: '#389e0d' }}
          />
          <Statistic
            title="Зарлага"
            value={withdrawalTotal}
            formatter={(v) => formatMNT(Number(v))}
            valueStyle={{ color: '#cf1322' }}
          />
          <Statistic
            title="Цэвэр"
            value={depositTotal - withdrawalTotal}
            formatter={(v) => formatMNT(Number(v))}
          />
        </div>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={data?.results ?? []}
          loading={loading}
          scroll={{ x: true }}
          pagination={{
            current: page + 1,
            pageSize,
            total: data?.total ?? 0,
            showSizeChanger: true,
            onChange: (p, size) => {
              setPage(size !== pageSize ? 0 : p - 1);
              setPageSize(size);
            },
          }}
        />
      </Card>

      {drawerKind && (
        <CashTransactionDrawer
          kind={drawerKind}
          onClose={() => setDrawerKind(null)}
          onDone={handleDone}
        />
      )}
    </LayoutWithMenu>
  );
}
